import styled from 'styled-components';
import { COLORS } from '../../../constants/radar';

export const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 16px;
`;

export const HeaderStats = styled.div`
  display: flex;
  align-items: flex-start;
`;

export const HeaderStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  white-space: nowrap;
  position: relative;
  padding: 0 16px;
  min-height: 36px;

  .top {
    display: flex;
    align-items: baseline;
    gap: 6px;
  }

  .label {
    font-size: 11px;
    color: #64748B;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
  }

  .value {
    font-size: 20px;
    font-weight: 700;
    color: ${COLORS.PRIMARY};
  }

  & + &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
    height: 24px;
    background: #e0e0e0;
  }
`;

// One row of the tree. Depth is expressed as padding so every row spans the
// full width and the hover surface stays continuous.
export const TreeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px 6px ${({ $depth }) => 16 + $depth * 24}px;
  border-bottom: 1px solid #f1f5f9;
  min-height: 40px;
  transition: background-color 0.15s;

  &:hover {
    background-color: #f8fafc;
  }
`;

// Fixed-width slot so rows without a toggle still align with rows that
// have one.
export const ExpandButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #64748B;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  padding: 0;

  &:hover {
    background-color: #e2e8f0;
    color: ${COLORS.PRIMARY};
  }

  svg {
    font-size: 11px;
  }
`;

export const ExpandSpacer = styled.span`
  width: 22px;
  flex-shrink: 0;
`;

// Node kind marker — colors carried over from the POC legend so the levels
// stay recognisable to existing users.
export const TypeTag = styled.span`
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: white;
  background: ${({ $color }) => $color};
  border-radius: 4px;
  padding: 2px 6px;
  flex-shrink: 0;
`;

// ADR-104: the Unallocated node is NOT a level of the structure, so it must
// not take a fifth colour in the TypeTag palette — that palette means "level".
// Outlined and muted: different in kind, not just in hue.
export const MutedTag = styled.span`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #94A3B8;
  background: transparent;
  border: 1px dashed #CBD5E1;
  border-radius: 4px;
  padding: 1px 7px;
  flex-shrink: 0;
`;

export const NodeName = styled.span`
  font-size: 14px;
  font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
  color: ${({ $bold, $muted }) => ($muted ? '#94A3B8' : $bold ? COLORS.PRIMARY : '#334155')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const NodeDetail = styled.span`
  font-size: 12px;
  color: #94A3B8;
  white-space: nowrap;
`;

// ADR-120: lifecycle marker on Business Unit rows (Ends … / Ended …).
export const LifecycleTag = styled.span`
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 4px;
  white-space: nowrap;
  color: ${({ $ending }) => ($ending ? '#B45309' : '#6B7280')};
  background: ${({ $ending }) => ($ending ? '#FEF3C7' : '#E5E7EB')};
`;

// Right-hand side of a row: owners/managers and counts.
export const RowAside = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

// An owner / manager rendered as avatar + name with a quiet role label.
export const PersonCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  .role {
    font-size: 10px;
    color: #94A3B8;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-weight: 600;
  }

  .name {
    font-size: 12px;
    color: #475569;
    font-weight: 500;
    white-space: nowrap;
  }
`;

export const CountTag = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #64748B;
  background: #F1F5F9;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 2px 8px;
  white-space: nowrap;
`;

const STATUS_COLORS = {
  engaged: { fg: '#166534', bg: '#DCFCE7' },
  joining: { fg: '#1E40AF', bg: '#DBEAFE' },
  leaving: { fg: '#92400E', bg: '#FEF3C7' },
  removed: { fg: '#64748B', bg: '#F1F5F9' },
  // ADR-104: rows in the Unallocated node carry the EMPLOYEE status
  // (ADR-017), not the allocation status — they have no allocation.
  employed: { fg: '#166534', bg: '#DCFCE7' },
  resigned: { fg: '#64748B', bg: '#F1F5F9' },
  unknown: { fg: '#64748B', bg: '#F1F5F9' },
};

export const StatusChip = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: capitalize;
  color: ${({ $status }) => (STATUS_COLORS[$status] || STATUS_COLORS.unknown).fg};
  background: ${({ $status }) => (STATUS_COLORS[$status] || STATUS_COLORS.unknown).bg};
  border-radius: 10px;
  padding: 2px 8px;
  white-space: nowrap;
`;

export const EngagementChip = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #475569;
  background: #E2E8F0;
  border-radius: 4px;
  padding: 2px 6px;
  white-space: nowrap;
`;

// Employee line under a project/team once allocations are loaded.
export const AllocationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  .name {
    font-size: 13px;
    color: #334155;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .meta {
    font-size: 11px;
    color: #94A3B8;
    white-space: nowrap;
  }
`;

// Transient row shown under an expanded project while its allocations load
// (or when the load failed).
export const InlineNote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${({ $error }) => ($error ? '#B91C1C' : '#94A3B8')};

  .retry {
    background: none;
    border: none;
    cursor: pointer;
    color: ${COLORS.PRIMARY};
    font-size: 12px;
    font-weight: 600;
    padding: 0;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;
